import React from 'react';
import { cn } from '@/lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FormFieldProps {
  label: string;
  labelExtra?: React.ReactNode;
  children: React.ReactNode;
  error?: string;
  required?: boolean;
  helpText?: string;
  title?: string;
  htmlFor?: string;
  className?: string;
  /** Optional id on the field wrapper — used as a scroll/focus anchor. */
  id?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function FormField({
  label,
  labelExtra,
  children,
  error,
  required,
  helpText,
  title,
  htmlFor,
  className,
  id,
}: FormFieldProps) {
  const errorId = error ? `field-error-${htmlFor ?? label.toLowerCase().replace(/\s+/g, '-')}` : undefined;
  const helpId = helpText ? `field-help-${htmlFor ?? label.toLowerCase().replace(/\s+/g, '-')}` : undefined;

  return (
    <div id={id} className={cn('flex flex-col gap-1 scroll-mt-4', className)} title={title}>
      {/* Label */}
      <label
        htmlFor={htmlFor}
        className="text-sm font-medium text-slate-700 select-none flex items-center gap-1"
      >
        {label}
        {required && (
          <span className="text-red-500" aria-hidden="true">
            *
          </span>
        )}
        {labelExtra}
      </label>

      {/* Input slot — children are responsible for referencing errorId/helpId if needed */}
      <div
        aria-describedby={[errorId, helpId].filter(Boolean).join(' ') || undefined}
      >
        {children}
      </div>

      {/* Help text */}
      {helpText && !error && (
        <p id={helpId} className="text-xs text-slate-500">
          {helpText}
        </p>
      )}

      {/* Error */}
      {error && (
        <p id={errorId} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
